import { useState, useRef } from 'react'
import { Button } from '@mui/material'
import OpenInpFileHeadless from '../SwmmReact_Staging/OpenInpFileHeadless'

/**
 * This is the collective top menu ribbon for generic UI controls like file->open, settings, saves, display options, etc.
 * 
 * @param {function} setModel state function, sets the JSON version of an EPA-SWMM model.
 * @param {function} setSwmmInp state function, loads local models for 'new' or 'demo' model types.
 * @param {function} setSwmmOut state function, sets the .out file for a model, in the case that a model is intended to be pre-loaded with one.
 * @param {string} rpt state variable, contents of an rpt file.
 * @param {function} setRpt state function, sets the contents of an rpt file.
 * @param {string} swmmTxt state variable, contents of a swmm .inp file.
 * @param {function} setEditType state function, used for unique edit effects like relocate and divide link. Not sure this should be in this menu.
 * @param {function} setModelRun state function, used as a flag to signal other components that the model results have changed, or should be considered as changed.
 * @param {function} setSwmmTxt state function, sets contents of a swmm .inp file
 * @param {function} setLoadModel state function, used as a flag to signal that a model should be loaded. This helps in cases where a model must be reloaded.
 * @param {function} handleClose closes most open editing dialogs.
 * @param {boolean} demos if true, shows an optional 'demos' menu option for opening a list of demo models.
 * @param {function} changeMapStyle state function, changes a MapBox map style (street, satellite, etc.)
 * @param {Object} dialogStates options for dialog styles.
 * @param {function} backgroundColor Color of dialog backgrounds
 * @param {boolean} loadNew if true, allows a user to load their own epa-swmm file.
 * @returns {JSX} menu interface for general file and option interactions.
 */
export default function MenuPrimary({setModel, setSwmmInp, setSwmmOut, rpt, setRpt, swmmTxt, setEditType, setModelRun, setSwmmTxt, setLoadModel, handleClose, demos, changeMapStyle, dialogStates, backgroundColor, loadNew, showLabels, setShowLabels}){
  const inputRef = useRef()
  const [fileNames] = useState([
    'subareaRouting',
    'All 57 culvert types example',
    'All outfall types',
    'Attenuation example',
    'Circular culvert Manning equation',
    'Critical downstream link offset example',
    'Critical upstream followed by critical downstream example',
    'Critical upstream link offset example',
    'CustomConduitShape',
    'DryPond_100yr24hr',
    'EPANET-SWMM Hazen Williams comparison',
    'exam1_negative_flow_examples',
    "Example 3 - Steven's Avenue Drainage District",
    'Example 6.11',
    'Flow through a constriction example',
    'GWI_DWF_Example',
    'Hourly evaporation example',
    'LID Underdrain Example SI units',
    'LID Underdrain Example US units',
    'LID_100yr24hr',
    'ParallelPumpConfiguration',
    /*'PCSWMM 3.2 Exercise 1C',*/
    'PID pump control example',
    'Pressure Main Example',
    'Rational Method vs SWMM5',
    'Simple green roof example',
    'Simple rain barrel example',
    'Simple rain garden example',
    'Simple rain garden WQ example',
    'Single event tutorial',
    'Six EXTRAN examples',
    'Steep sloped - adverse sloped pipe example',
    'Storage units vs junctions (slot method)',
    'Storm sewer system Manning equation',
    'Subcatchment width sensitivity',
    'TeardropPark_DiscreteOrificeControl',
    'TeardropPark_ModulatedOrificeControl_Curve',
    'TeardropPark_ModulatedOrificeControl_Timeseries',
    'Trapezoidal channel 1 Manning equation',
    'Trapezoidal channel 2 Manning equation',
    'UV plant with detention ponds',
    'VariableSoilTexture_SoilSaturation',
    'VSP rules for 3 pumps',
    'Weir equations (SI units)',
    'Weir equations (US units)',
    'WQInitialBuildup'
  ])

  // Top menu ribbon
  const topMenuRibbonStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    borderBottom: "1px solid #333", 
    marginBottom: '0px',
    paddingLeft: '5px',  
    zIndex: 10001,
    left: '0', 
    top: '0', 
  }

  // File menu button
  const topMenuButtonStyle = { 
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    paddingBottom: '0px',
    paddingLeft: '10px', 
    paddingTop: '0px', 
  }

  // menu-button element
  const menuButtonStyle = { 
    width: '100%'
  }

  // right-menu element
  const rightMenuStyle = { 
    zIndex: 10000,
    width: '150px',
    backgroundColor: backgroundColor
  }

  // Demo file list
  const demoFileListStyle = { 
    width: '500px', 
    height:'500px', 
    overflowY:'scroll', 
    overflowX: 'hidden',
    backgroundColor: backgroundColor
  }
  
  // Demo file element
  const demoFileElementStyle = { 
    width: '500px'
  }

  // Function on 'load' click
  const openInpFileHeadless = OpenInpFileHeadless(setModel, setSwmmTxt, setSwmmOut, setRpt, setLoadModel, handleClose)

  return (
    <div className='controlbar' style={topMenuRibbonStyle}>
    <div className="add-node-vertical"  >
      <Button className='menu-button' style={topMenuButtonStyle} >File</Button>
      <ul className="right-menu" style={rightMenuStyle}>
        {
          loadNew &&
          <>
            <li>
              <Button className='menu-button' style={menuButtonStyle} onClick={()=>{setSwmmInp('./extraResources/newFile.inp')}}>New</Button>
            </li>
            <li>
              <Button  className='menu-button' style={menuButtonStyle} onClick={()=>inputRef.current.click()}>Open</Button>
              <input 
                style={{display: 'none'}}
                ref={inputRef}
                type="file"
                accept=".inp"
                onChange={(e) => {openInpFileHeadless.showFile(e).then(()=>{inputRef.current.value = null;})}}
              />
            </li>
          </>
        }
        <li>
          <Button className='menu-button' href={`data:text/json;charset=utf-8,${encodeURIComponent(swmmTxt)}`} style={menuButtonStyle} download="model.inp" >Save Inp</Button>
        </li>
        <li>
          <Button className='menu-button' href={`data:text/json;charset=utf-8,${encodeURIComponent(rpt?.replace(/\0/g, ''))}`} style={menuButtonStyle} download="report.rpt" >Save Report</Button>
        </li>
        {
          demos &&
          <div className="sublist">
            <Button className='menu-button' style={rightMenuStyle} >Demos</Button>
            <ul className="right-menu main" style={demoFileListStyle}>
              {fileNames.map((val)=>(
                <li key={val} >
                  <Button className='menu-button' style={demoFileElementStyle} onClick={()=>{setSwmmInp('./extraResources/'+val+'.inp')}}>{val}</Button>
                </li>
              ))}
            </ul>
          </div>
        }   
      </ul>
    </div>
    <div className="add-node-vertical"  >
      <Button className='menu-button' style={topMenuButtonStyle} >Run</Button>
      <ul className="right-menu" style={rightMenuStyle}>
        <li>
          <Button className='menu-button' style={menuButtonStyle} onClick={()=>{setModelRun(prev=>prev+1)}} >Run</Button>
        </li>
      </ul>
    </div>
    <div className="add-node-vertical"  >
      <Button className='menu-button' style={topMenuButtonStyle} >Report</Button>
      <ul className="right-menu" style={rightMenuStyle}>
        <li>
          <Button className='menu-button' style={menuButtonStyle} onClick={()=>{dialogStates['RptText'].setOpen(true)}}>.rpt File</Button>
        </li>
        <li>
          <Button className='menu-button' style={menuButtonStyle} onClick={()=>{dialogStates['ModelText'].setOpen(true)}}>Model</Button>
        </li>
      </ul>
    </div>
    <div className="add-node-vertical"  >
      <Button className='menu-button' style={topMenuButtonStyle} >Utility</Button>
      <ul className="right-menu" style={rightMenuStyle}>
        {/*<li><Button className='menu-button' style={menuButtonStyle} onClick={()=>{setEditType(1)}} >Profile</Button></li>*/}
        <li><Button className='menu-button' style={menuButtonStyle} onClick={()=>{setEditType(1)}} >Relocate</Button></li>
        <li><Button className='menu-button' style={menuButtonStyle} onClick={()=>{handleClose(); setEditType(1001)}}>Divide Link</Button></li>
        <li><Button className='menu-button' style={menuButtonStyle} onClick={()=>{dialogStates['Colors'].setOpen(true)}} >Colors</Button></li>
      </ul>
    </div>
    {
      <div className="add-node-vertical"  >
        <Button className='menu-button' style={topMenuButtonStyle} >Map</Button>
        <ul className="right-menu" style={rightMenuStyle}>
          <li>
            <Button className='menu-button' style={menuButtonStyle} onClick={()=>{changeMapStyle()}} >Style</Button>
          </li>
          <li>
            <Button className='menu-button' style={menuButtonStyle} onClick={()=>{setShowLabels(prev=>!prev)}} >Show Labels</Button>
          </li>
        </ul>
      </div>
    }
    </div>
  )
}
